// §6 — Closing Documents (DERIVED checklist). Ported from Documents.jsx
// DocumentsSection. The derive pipeline (deriveVesting/Eoi/Appraisal/Icd/HoaMaster)
// runs server-side; rows arrive already derived as `docs: ChecklistDoc[]`. P4 adds
// the upload affordances the prototype carried (DocUploadBtn + InvoiceMissingRow):
// missing §6 docs (except Initial CD / Appraisal — never uploaded here) expand the
// shared UploadPanel (doc mode); cleared docs can be Reset (reopened) for a
// replacement; the fee→invoice rollup mounts the invoice UploadPanel per missing
// fee. Statuses stay server-derived from the eFolder mirror — the panel never
// flips a row locally; the queue lights it after the real upload. Staleness (ESS /
// title shelf-life) + the rollup come from core helpers (zero reimplementation).
import { useState } from 'react';
import { Icon, StatusPill } from '../atoms';
import { useDocOrderCtx } from '../context';
import { fmtDate, fmtMoney, fmtStamp } from '../format';
import {
  essIsStale,
  titleStaleAtClosing,
  daysBetween,
  feeInvoiceStatus,
  feeInvoiceWorkflowOpen,
  DO_DOC_EFOLDER_MAP,
  type ChecklistDoc,
  type DocOrderFee
} from '../core';
import { UploadPanel } from './UploadPanel';

interface DocumentsProps {
  docs: ChecklistDoc[];
  fees: DocOrderFee[];
  stepNo: number;
}

const DOC_STATUS_ICON: Record<ChecklistDoc['status'], string> = {
  received: 'fileCheck',
  pending: 'clock',
  missing: 'alertTriangle'
};

// Initial CD (Field 3977 only) and Appraisal (eFolder / PIW) are never uploaded
// from this section — their status is derived from other Encompass signals.
const NO_UPLOAD_DOCS = new Set(['d-icd', 'd-appr']);

// Rollup row for a charged fee still missing its eFolder invoice. Ported from the
// prototype InvoiceMissingRow (Documents.jsx:345-381): fee + amount + an upload
// affordance that expands the shared invoice UploadPanel, plus the server-stamped
// AI rejection banner that keeps the requirement open until a good invoice lands.
function InvoiceMissingRow({ fee, fees }: { fee: DocOrderFee; fees: DocOrderFee[] }) {
  const { editable } = useDocOrderCtx();
  const [uploading, setUploading] = useState(false);
  const reject = fee.acks?.invoiceReject ?? null;
  // Status-aware CTA (Batch 3.1 consistency): a queued/held/failed upload names
  // its state — this rollup only renders while the requirement is OPEN, so
  // filed evidence never competes here. Invoice entries are array-backed;
  // uploading another while one is queued is legitimate.
  const entries = fee.invoices ?? [];
  const heldUpload = entries.some((i) => i.uploadStatus === 'queued' && i.uploadError);
  const queuedUpload = entries.some((i) => i.uploadStatus === 'queued');
  const failedUpload = entries.some((i) => i.uploadStatus === 'failed');
  const ctaTitle = heldUpload
    ? 'Upload held — the loan is open in Encompass; it files when the lock clears. You can upload another.'
    : queuedUpload
      ? 'An invoice upload is in progress — not filed yet. You can upload another.'
      : failedUpload
        ? 'Last invoice upload failed — upload again'
        : 'Upload the invoice — AI compares the amount to the fee';
  const ctaLabel = queuedUpload ? 'Upload another' : reject || failedUpload ? 'Re-upload invoice' : 'Upload invoice';

  return (
    <div className={`invoice-missing-row ${uploading ? 'is-uploading' : ''} ${reject ? 'flagged' : ''}`.trim()}>
      <div className="im-main">
        <span className="im-name">
          <Icon name={reject ? 'alertTriangle' : 'alertCircle'} size={13} style={{ color: 'var(--bad-fg)' }} />
          {fee.name}
          {fee.payee ? <span className="im-payee"> · {fee.payee}</span> : null}
        </span>
        <div className="im-right">
          <span className="im-amt">{fmtMoney(fee.amount, true)}</span>
          {editable && !uploading && (
            <button className="im-upload-btn" onClick={() => setUploading(true)} title={ctaTitle}>
              <Icon name="upload" size={11} />
              {ctaLabel}
            </button>
          )}
        </div>
      </div>
      {reject && (
        <div className="im-reject">
          <Icon name="alertTriangle" size={11} />
          <span>
            Validation failed on “{reject.file}”{reject.detected ? ` — reads as a ${reject.detected}` : ''}. Requirement reopened —
            re-upload the correct invoice.
            {reject.afterSubmit && <b> Flagged to Closing.</b>}
          </span>
        </div>
      )}
      {uploading && <UploadPanel mode="invoice" fee={fee} fees={fees} onClose={() => setUploading(false)} />}
    </div>
  );
}

export function DocumentsSection({ docs, fees, stepNo }: DocumentsProps) {
  const { now, closingDate, invoiceFolderStatus, editable, busy, resetDocument, addToast, checkUploadStatus, checkingUploads } = useDocOrderCtx();
  // Which missing §6 doc has its inline UploadPanel (doc mode) expanded.
  const [uploadingDocId, setUploadingDocId] = useState<string | null>(null);

  const received = docs.filter((d) => d.status === 'received').length;
  const missingReq = docs.filter((d) => d.required && d.status === 'missing');

  // Reopen a cleared doc for a replacement upload — the server flips it back to
  // missing (reopened override) and the aggregate refresh re-renders it.
  const handleReset = async (d: ChecklistDoc) => {
    const res = await resetDocument(d.id);
    if (res.ok) {
      addToast(`${d.name} reopened for upload`, 'refresh');
    }
  };

  // Fee → invoice rollup. feeInvoiceStatus folds in the $0 / notApplicable
  // exclusion ('na') and is filed-only + rejection-aware: a charged, applicable
  // fee is missing its invoice when its eFolder folder has no current doc AND no
  // filed upload, or a prior upload was AI-rejected (invoiceReject reopens it).
  const chargedFees = fees.filter((f) => feeInvoiceStatus(f, !!invoiceFolderStatus[f.feeKey]) !== 'na');
  // R5 #3 — EVIDENCE and WORKFLOW are separate truths (review-2 t8 + review-3
  // #5): the matched COUNT stays evidence-honest (an escalation never counts
  // as an eFolder invoice); the OUTSTANDING list keys on the workflow rule;
  // escalated fees render their own per-fee "resolved by escalation" rows
  // (fee, note, actor, timestamp, unresolved evidence state).
  const missingEvidence = chargedFees.filter((f) => feeInvoiceStatus(f, !!invoiceFolderStatus[f.feeKey]) === 'missing');
  const workflowOutstanding = missingEvidence.filter((f) => feeInvoiceWorkflowOpen(f, !!invoiceFolderStatus[f.feeKey]));
  const escalatedResolved = missingEvidence.filter((f) => !feeInvoiceWorkflowOpen(f, !!invoiceFolderStatus[f.feeKey]));
  const matchedInvoices = chargedFees.length - missingEvidence.length;
  // Rejections stamped after the order left Draft are flagged on Closing's screen.
  const flaggedToClosing = workflowOutstanding.filter((f) => f.acks?.invoiceReject?.afterSubmit);

  return (
    <section className="ws-section" id="sec-docs">
      <div className="card card-pad snapshot-compact">
        <div className="ws-section-head">
          <h3 className="ws-section-title"><span className="ws-step-no">{stepNo}</span>Closing Documents</h3>
          <StatusPill kind={missingReq.length ? 'warn' : 'ok'} icon="folder">{received}/{docs.length} in eFolder</StatusPill>
        </div>
        <div className="banner info" style={{ marginBottom: 10, padding: '6px 10px', fontSize: 11 }}>
          <Icon name="refresh" size={12} className="banner-ico" />
          <span>Auto-detected from the Encompass eFolder</span>
        </div>
        <div className="doc-list">
          {docs.map((d) => {
            // A QUEUED checklist upload blocks a second one (Batch 3.1 rollout
            // blocker): the row's upload state is a SINGULAR pointer — another
            // accepted upload would replace it while the first queue job still
            // files, leaving a duplicate eFolder doc + an untracked upload. The
            // queued line shows its status + Check status instead; a FAILED
            // upload keeps the affordance (that IS the re-upload path).
            const uploadQueued = d.upload?.status === 'queued';
            const uploadFailed = d.upload?.status === 'failed';
            const canUpload = editable && !NO_UPLOAD_DOCS.has(d.id) && !uploadQueued;
            const docFolder = DO_DOC_EFOLDER_MAP[d.id] ?? d.name;
            return (
              <div key={d.id} className="doc-item-wrap">
                <div className={`doc-item ${d.status}`}>
                  <div className={`doc-status-ico ${d.status}`}><Icon name={DOC_STATUS_ICON[d.status]} size={13} /></div>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div className="doc-name">
                      {d.name}
                      {d.note && (
                        <span className="doc-note-tip" title={d.note} tabIndex={0} aria-label={`Details about ${d.name}`}>
                          <Icon name="alertCircle" size={11} />
                        </span>
                      )}
                      {/* Stale flags (core helpers gate; daysBetween labels):
                            - Estimated Settlement Statement (d-cd): age vs now ≥ essStaleDays.
                            - Title Commitment (d-title): age AT CLOSING > titleShelfLifeDays. */}
                      {(() => {
                        if (d.status !== 'received' || !d.receivedAt) {
                          return null;
                        }
                        if (d.id === 'd-cd' && essIsStale(d.receivedAt, now)) {
                          const ageDays = daysBetween(d.receivedAt, now);
                          if (ageDays === null || ageDays === undefined) {
                            return null;
                          }
                          return (
                            <span className="chip chip-stale" title={`Document is ${ageDays} days old — request an updated estimated settlement statement before drawing docs.`}>
                              <Icon name="alertTriangle" size={9} />Stale · {ageDays}d
                            </span>
                          );
                        }
                        if (d.id === 'd-title' && titleStaleAtClosing(d.receivedAt, closingDate)) {
                          const ageAtClose = daysBetween(d.receivedAt, closingDate);
                          if (ageAtClose === null || ageAtClose === undefined) {
                            return null;
                          }
                          return (
                            <span className="chip chip-stale" title="Title commitment will expire prior to the closing date. Request an updated commitment.">
                              <Icon name="alertTriangle" size={9} />Stale · {ageAtClose}d
                            </span>
                          );
                        }
                        return null;
                      })()}
                    </div>
                    <div className="doc-meta">
                      {d.status === 'received' && d.deferred && d.id === 'd-eoi' ? (
                        <span className="doc-auto doc-deferred">
                          <Icon name="clock" size={10} />Deferred along with Section 4 — auto-clears when the section is resumed and binders are received.
                        </span>
                      ) : d.status === 'received' ? (
                        <>
                          {/* R5 (review-3 #7c): STRUCTURED evidence labels —
                              cd_sent must never claim "Received"; the GFE
                              waiver names itself; PIW names its field. */}
                          {d.evidence?.source === 'cd_received'
                            ? <>Received {fmtDate(d.evidence.at)}</>
                            : d.evidence?.source === 'cd_sent'
                              ? <>Sent {fmtDate(d.evidence.at)}</>
                              : d.evidence?.source === 'piw-4954'
                                ? <>Cleared via PIW — Field 4954</>
                                : d.evidence?.source === 'business-purpose'
                                  ? <>Waived — business purpose (GFE {fmtDate(d.evidence.at)})</>
                                  : d.receivedAt
                                    ? <>Received {fmtDate(d.receivedAt)}</>
                                    : d.auto?.source === 'piw-4954'
                                      ? <>Cleared via PIW — Field 4954</>
                                      : d.auto
                                        ? <>Auto-cleared</>
                                        : <>Received —</>}{d.file && <> · <span className="doc-file">{d.file}</span></>}
                          {d.aiWarn && d.aiWarn.length > 0 && (
                            <div className="doc-eoi-subchecks">
                              {d.aiWarn.map((w, i) => (
                                <span key={i} className="doc-subcheck warn">
                                  <Icon name="alertTriangle" size={9} />{w}
                                </span>
                              ))}
                            </div>
                          )}
                          {d.id === 'd-eoi' && d.checks && d.checks.length > 1 && (
                            <div className="doc-eoi-subchecks">
                              {d.checks.map((c, i) => (
                                <span key={i} className={`doc-subcheck ${c.ok ? 'ok' : 'bad'}`}>
                                  <Icon name={c.ok ? 'check' : 'x'} size={9} />{c.label}{c.file && <span className="doc-file" style={{ marginLeft: 4 }}>{c.file}</span>}
                                </span>
                              ))}
                            </div>
                          )}
                        </>
                      ) : null}
                      {d.status === 'pending' && 'In file — not yet final'}
                      {d.status === 'missing' && (
                        <>
                          {d.id === 'd-eoi' && d.checks && d.checks.length > 0 && (
                            <div className="doc-eoi-subchecks">
                              {d.checks.map((c, i) => (
                                <span key={i} className={`doc-subcheck ${c.ok ? 'ok' : 'bad'}`}>
                                  <Icon name={c.ok ? 'check' : 'x'} size={9} />{c.label}
                                </span>
                              ))}
                              {!d.section4Complete && (
                                <span className="doc-subcheck bad">
                                  <Icon name="x" size={9} />Section 4 fields
                                </span>
                              )}
                            </div>
                          )}
                          {d.pendingChecks && d.pendingChecks.length > 0 && (
                            <div className="doc-eoi-subchecks">
                              {d.pendingChecks.map((label, i) => (
                                <span key={i} className="doc-subcheck bad">
                                  <Icon name="x" size={9} />{label}
                                </span>
                              ))}
                            </div>
                          )}
                          {d.auditReject && (
                            <div className="doc-audit-fail">
                              <Icon name="alertTriangle" size={10} />AI audit rejected “{d.auditReject.file}” — {d.auditReject.reason} Not filed to the eFolder.
                            </div>
                          )}
                          {/* Durable queue state (round-25 P1): queued renders the
                              converging note; failed offers the re-upload path.
                              Neither makes the row read received. */}
                          {d.upload?.status === 'queued' && d.status === 'missing' && (
                            <div className="doc-upload-queued">
                              {/* #5: a held upload (loan open in Encompass) carries a
                                  reason on the still-queued row — show it instead of
                                  a bare "Uploading…" that never resolves. Check status
                                  runs the lightweight convergence (F4 — never the
                                  full refresh). */}
                              <Icon name="clock" size={10} />
                              {d.upload.error
                                ? <>“{d.upload.file}” — {d.upload.error}</>
                                : <>Uploading “{d.upload.file}” to Encompass…</>}
                              <button
                                type="button"
                                className="doc-check-status"
                                disabled={checkingUploads}
                                onClick={() => void checkUploadStatus()}
                              >
                                {checkingUploads ? 'Checking…' : 'Check status'}
                              </button>
                            </div>
                          )}
                          {d.upload?.status === 'failed' && d.status === 'missing' && (
                            <div className="doc-audit-fail">
                              <Icon name="alertTriangle" size={10} />Upload of “{d.upload.file}” failed — {d.upload.error || 'eFolder upload failed'}. Re-upload the document.
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                  <div className="doc-confirm">
                    {d.status === 'missing' ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <StatusPill kind="bad" icon="x">Missing</StatusPill>
                        {canUpload && (
                          <button
                            type="button"
                            className={`fee-upload-btn doc-upload-btn ${uploadingDocId === d.id ? 'is-open' : ''}`.trim()}
                            title={
                              uploadFailed
                                ? `Last upload failed — upload a replacement · ${docFolder}`
                                : `Upload to Encompass eFolder · ${docFolder}`
                            }
                            aria-label={`Upload ${d.name} to the Encompass eFolder document "${docFolder}"`}
                            onClick={() => setUploadingDocId(uploadingDocId === d.id ? null : d.id)}
                          >
                            <Icon name="upload" size={12} />
                          </button>
                        )}
                      </span>
                    ) : d.status === 'pending' ? (
                      <StatusPill kind="warn" icon="clock">Pending</StatusPill>
                    ) : (
                      <span className="doc-cleared">
                        <span
                          className="doc-cleared-ico"
                          title={d.auto ? `Cleared automatically — ${d.auto.reason}` : 'Cleared automatically from the eFolder'}
                        >
                          <Icon name="refreshCheck" size={16} />
                        </span>
                        {editable && d.file && !NO_UPLOAD_DOCS.has(d.id) && !d.deferred && (
                          <button
                            type="button"
                            className="doc-reset-btn"
                            title="Reopen this document to upload a replacement"
                            disabled={busy}
                            onClick={() => void handleReset(d)}
                          >
                            <Icon name="refresh" size={11} />Reset document
                          </button>
                        )}
                      </span>
                    )}
                  </div>
                </div>
                {canUpload && uploadingDocId === d.id && d.status === 'missing' && (
                  <div className="doc-row-upload">
                    <UploadPanel mode="doc" doc={d} onClose={() => setUploadingDocId(null)} />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Fee → invoice check: flags any charged fee missing its eFolder invoice */}
        {chargedFees.length > 0 && (
          <div className="invoice-check">
            <div className="invoice-check-head">
              <span className="section-title"><Icon name="fileText" size={13} /> Fee Invoices in eFolder</span>
              <StatusPill kind={workflowOutstanding.length ? 'bad' : 'ok'} icon={workflowOutstanding.length ? 'alertTriangle' : 'check'}>
                {matchedInvoices}/{chargedFees.length} matched
              </StatusPill>
            </div>
            {missingEvidence.length === 0 ? (
              // The green all-clear claims REAL eFolder evidence — it never
              // renders over an escalation waiver (review-2 tightening 8).
              <div className="banner ok" style={{ background: 'var(--ok-bg)', color: 'var(--ok-fg)' }}>
                <Icon name="checkCircle" size={16} className="banner-ico" />
                <span>Every entered fee has a matching invoice in the eFolder.</span>
              </div>
            ) : (
              <>
                {workflowOutstanding.length > 0 && (
                  <div className="banner warn" style={{ display: 'block' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                      <Icon name="alertTriangle" size={16} className="banner-ico" />
                      <div><b>{workflowOutstanding.length} fee{workflowOutstanding.length > 1 ? 's are' : ' is'} missing an invoice.</b> Upload the invoice to clear this requirement.</div>
                    </div>
                    {flaggedToClosing.length > 0 && (
                      <div className="invoice-closing-flag">
                        <Icon name="flag" size={13} />
                        <span>This order was already submitted to Closing. They have been notified and the flagged invoice{flaggedToClosing.length > 1 ? 's are' : ' is'} highlighted below.</span>
                      </div>
                    )}
                    <div className="invoice-missing-list">
                      {workflowOutstanding.map((f) => (
                        <InvoiceMissingRow key={f.id} fee={f} fees={fees} />
                      ))}
                    </div>
                  </div>
                )}
                {escalatedResolved.length > 0 && (
                  <div className="invoice-escalated-list">
                    <div className="invoice-escalated-head">
                      <Icon name="flag" size={13} />
                      <span>
                        {escalatedResolved.length} resolved by missing-invoice escalation — no invoice in the eFolder.
                      </span>
                    </div>
                    {escalatedResolved.map((f) => {
                      const esc = f.acks?.escalation;
                      return (
                        <div key={f.id} className="invoice-escalated-row">
                          <span className="inv-esc-fee">{f.name}</span>
                          <span className="inv-esc-note">&ldquo;{esc?.note}&rdquo;</span>
                          <span className="inv-esc-stamp">
                            {esc?.by} · {fmtStamp(esc?.at)} · no invoice in the eFolder
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
